import View                 from '@widesk-ui/components/View';
import { observer }         from 'mobx-react-lite';
import { HEADER_HEIGHT }    from '@/TokamapEditor/ux/constants';
import { SHOW_PLAN_IMPORT } from '@/TokamapEditor/ux/constants';
import MapButton            from '@/TokamapEditor/ux/MapMenu/MapButton';
import UndoRedo             from '@/TokamapEditor/ux/UndoRedo';
import ShapesButton         from '@/TokamapEditor/ux/ShapesButton';
import SettingsButton       from '@/TokamapEditor/ux/SettingsButton';
import PlanImportButtons    from '@/TokamapEditor/ux/PlanImportButtons';
import { RULER_SIZE_PX }    from '@/TokamapEditor/ux/PlanRulers';
import currentEditorStore   from '@/TokamapEditor/stores/currentEditorStore';
import useDesignTokens      from '@/hooks/useDesignTokens';
import MergeButton          from '@/TokamapEditor/ux/MergeButton';

export const FLOATING_BAR_HEIGHT = 40;

// Barre flottant par-dessus le plan : tout ce qui concerne la carte elle-même (savoir laquelle est
// ouverte, en changer, y dessiner, annuler/rétablir ses modifications, régler son affichage), là où
// la barre du haut ne garde que ce qui concerne le projet. Elle a remplacé la barre d'outils
// verticale de gauche, qui n'existait plus que pour ces deux dernières entrées.
// Ancrée à droite pour ne jamais passer sous la sidebar de gauche.
//
// Un seul conteneur (une bordure, une ombre, un rayon) segmenté par un filet vertical : les
// sous-parties se dessinent à plat, sans fond ni ombre propres, pour que l'ensemble se lise comme
// un objet unique et non comme des boutons empilés.
const MapFloatingControls = observer(() => {
	const tokens = useDesignTokens();

	// Même condition que la garde interne d'`UndoRedo` : sans elle, le filet resterait seul contre le
	// bord de la barre tant que la carte n'a pas de centre (il n'y a alors rien à annuler). Les outils
	// de dessin suivent la même règle (elle était déjà celle de l'ancienne barre de gauche) : rien à
	// dessiner tant que le centre de la carte n'est pas posé.
	const mapReady = currentEditorStore.isCenterDefined && !!currentEditorStore.get;

	return (
		<View
			row
			h={FLOATING_BAR_HEIGHT}
			bg={tokens.surface.card}
			br={10}
			style={{
				position: 'absolute',
				top: HEADER_HEIGHT + 10 + (currentEditorStore.showsRulers ? RULER_SIZE_PX : 0),
				right: 10,
				zIndex: 5,
				// Les zones de survol des sous-parties vont d'un bord à l'autre : sans ça elles
				// déborderaient des coins arrondis.
				overflow: 'hidden',
				// Aucun texte de cette barre n'est là pour être copié (nom de la carte, "Formes"…), et
				// elle flotte au-dessus du plan : un glisser qui la traverse sélectionnait ces libellés
				// au lieu de rester un geste sur la carte.
				userSelect: 'none',
				border: `1px solid ${tokens.border.subtle}`,
				boxShadow: tokens.shadow.elevated,
			}}
		>
			{/* Les outils d'abord — dessiner, annuler —, puis ce qui porte sur la carte en tant qu'objet :
			    laquelle est ouverte, et ses réglages. Les deux derniers segments se tiennent ainsi
			    ensemble au lieu d'encadrer les outils. */}
			{mapReady && (
				<>
					<MergeButton separator={<Separator />} />

					<ShapesButton />

					<Separator />

					{SHOW_PLAN_IMPORT && (
						<>
							<PlanImportButtons />

							<Separator />
						</>
					)}

					<UndoRedo />

					<Separator />
				</>
			)}

			<MapButton />

			{mapReady && (
				<>
					<Separator />

					<SettingsButton />
				</>
			)}
		</View>
	);
});

// Filet vertical entre deux segments — voir le commentaire du composant.
const Separator = () => {
	const tokens = useDesignTokens();

	return <View bg={tokens.border.subtle} style={{ flex: '0 0 1px' }} />;
};

export default MapFloatingControls;
